import React, { Suspense, lazy, useEffect, useState } from "react";
import { Provider } from "react-redux";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ShoppingCartProvider } from "./components/_default/context/ShoppingCartContext";
import store from "./store/store";
import axios from "axios";
import { configureSentryContext, Sentry } from "./sentry";

import "./sass/app.scss";

const urlParams = new URLSearchParams(window.location.search);
const broadcastChannel = new BroadcastChannel("local_storage_channel");
const Builder = lazy(() => import("./pages/Builder"));

// Helper functions to get and set localStorage with expiry
function getExpiry(key) {
  const itemStr = localStorage.getItem(key);
  if (!itemStr) return null;
  try {
    const item = JSON.parse(itemStr);
    // Check if it's a valid expiry object with expiry property
    if (item && typeof item === 'object' && 'expiry' in item) {
      const now = new Date();
      if (now.getTime() > item.expiry) {
        localStorage.removeItem(key);
        return null;
      }
      return item.value;
    }
    // If it's not an expiry object, return null (invalid format)
    return null;
  } catch (e) {
    // If parsing fails, the value is not valid JSON (might be a plain string)
    // Remove the invalid entry to prevent future errors
    localStorage.removeItem(key);
    return null;
  }
}

function setExpiry(key, value, ttl) {
  if (value === null || value === undefined) return; // Prevent setting null/undefined values
  const now = new Date();
  const item = {
    value: value,
    expiry: now.getTime() + ttl,
  };
  localStorage.setItem(key, JSON.stringify(item));
  broadcastChannel.postMessage("localStorageUpdated");
}

// Initialize parameters and localStorage values
let Authorization = getExpiry("Authorization");
let APIKey = getExpiry("APIKey");
let APIID = getExpiry("APIID");
let websiteId = getExpiry("websiteId");
let websiteUuid = getExpiry("websiteUuid");
let APIUrl = getExpiry("APIUrl");
let baseURL = getExpiry("baseURL");
// backendURL should be persistent (no expiry) since it's needed for the continue button
let backendURL = localStorage.getItem("backendURL");

if (
  !Authorization ||
  !APIKey ||
  !APIID ||
  !websiteId ||
  !APIUrl ||
  urlParams.get("Authorization") ||
  urlParams.get("APIKey") ||
  urlParams.get("APIID") ||
  urlParams.get("websiteId") ||
  urlParams.get("websiteUuid") ||
  urlParams.get("APIUrl")
) {
  Authorization = urlParams.get("Authorization") || Authorization;
  setExpiry("Authorization", Authorization, 7 * 24 * 60 * 60 * 1000);

  APIKey = urlParams.get("APIKey") || APIKey;
  setExpiry("APIKey", APIKey, 7 * 24 * 60 * 60 * 1000);

  APIID = urlParams.get("APIID") || APIID;
  setExpiry("APIID", APIID, 7 * 24 * 60 * 60 * 1000);

  websiteId = urlParams.get("websiteId") || websiteId;
  setExpiry("websiteId", websiteId, 7 * 24 * 60 * 60 * 1000);

  websiteUuid = urlParams.get("websiteUuid") || websiteUuid;
  if (websiteUuid) {
    setExpiry("websiteUuid", websiteUuid, 7 * 24 * 60 * 60 * 1000);
  }

  backendURL = urlParams.get("backendURL") || backendURL;
  // Store backendURL persistently (no expiry) since it's needed for continue button
  if (backendURL) {
    localStorage.setItem("backendURL", backendURL);
  }

  window.websiteId = websiteId;

  APIUrl = `${backendURL}/builderApi/${websiteId}`;
  setExpiry("APIUrl", APIUrl, 7 * 24 * 60 * 60 * 1000);

  baseURL = `${backendURL}/webstoreApi`;
  setExpiry("baseURL", baseURL, 7 * 24 * 60 * 60 * 1000);

  // Clear URL parameters after storing them
  if (window.history.replaceState) {
    const cleanUrl = window.location.origin + window.location.pathname;
    window.history.replaceState(null, null, cleanUrl);
  }
}

if (websiteUuid) {
  window.websiteUuid = websiteUuid;
}

// Set up axios with appropriate headers
var axiosHeaders = {
  "Access-api-key": APIKey,
  "Access-api-id": APIID,
  "Access-Control-Allow-Origin": "*",
};

window.axios = axios.create({
  baseURL: baseURL,
  headers: axiosHeaders,
});

// Start products.json fetch before React mounts so builder catalog is ready sooner.
const catalogProductsNoCacheHeaders = {
  "Content-Type": "application/json",
  "Cache-Control": "no-cache, no-store, must-revalidate",
  Pragma: "no-cache",
  Expires: "0",
};

window.__catalogProductsPrefetch = window.axios
  .get("/products.json", {
    params: { _ts: Date.now() },
    headers: catalogProductsNoCacheHeaders,
  })
  .then((res) => res.data)
  .catch((err) => {
    delete window.__catalogProductsPrefetch;
    throw err;
  });

const APIUrlV2 =
  websiteUuid && backendURL
    ? `${backendURL}/builderApi/v2/${websiteUuid}`
    : undefined;

const APIUrlUuid =
  websiteUuid && backendURL
    ? `${backendURL}/builderApi/${websiteUuid}`
    : undefined;

window.API = {
  url: APIUrl,
  urlUuid: APIUrlUuid,
  urlV2: APIUrlV2,
  backendURL: backendURL,
};

window.defaultApiConfig = {
  mode: "cors",
  headers: {
    Authorization: Authorization,
    "Access-api-key": APIKey,
    "Access-api-id": APIID,
    "Access-Control-Allow-Origin": "*",
  },
};

// Importing Editor component
import Editor from "./components/BuilderComponents/Editors/Editor/Editor";
import { defaultComponents } from "./components/_default/interfaces/DefaultComponents";
defaultComponents.editor = Editor;
window.defaultComponents = defaultComponents;

function BuilderErrorFallback() {
  return (
    <div
      style={{
        height: "100vh",
        width: "100vw",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <span style={{ fontSize: "16px" }}>
        <b>ERROR</b> - Something went wrong in the builder
      </span>
      <span
        style={{
          fontSize: "12px",
          marginBottom: "10px",
          textAlign: "center",
        }}
      >
        The error has been reported. Please refresh the page or open the builder again.
      </span>
    </div>
  );
}

export default function App() {
  const [expired, setExpired] = useState(false);

  const handleLogin = () => {
    const url = new URL(window.location.href);
    const backendURL =
      url.searchParams.get("backendURL") || localStorage.getItem("backendURL");
    if (!backendURL) {
      alert("No URL found. Please try again.");
      return;
    }
    window.location.href = backendURL;
  };

  useEffect(() => {
    if (
      !Authorization ||
      !APIKey ||
      !APIID ||
      !websiteId ||
      !backendURL ||
      expired
    ) {
      return;
    }

    configureSentryContext({
      backendURL,
      websiteId,
      websiteUuid,
    });
  }, [expired]);

  useEffect(() => {
    const checkExpiry = () => {
      console.log("checkexpiry ran");

      if (
        !getExpiry("Authorization") ||
        !getExpiry("APIKey") ||
        !getExpiry("APIID") ||
        !getExpiry("websiteId") ||
        !localStorage.getItem("backendURL")
      ) {
        setExpired(true);
      }
    };

    const handleBroadcast = (message) => {
      if (message.data === "localStorageUpdated" && !expired) {
        checkExpiry();
        if (!expired) {
          window.location.reload();
        }
      }
    };

    checkExpiry();
    broadcastChannel.addEventListener("message", handleBroadcast);
    const interval = setInterval(checkExpiry, 60000); // Check every 60 seconds

    return () => {
      clearInterval(interval);
      broadcastChannel.removeEventListener("message", handleBroadcast);
    };
  }, [expired]);

  // If authorization credentials are missing or expired, show an error page
  if (
    !Authorization ||
    !APIKey ||
    !APIID ||
    !websiteId ||
    !backendURL ||
    expired
  ) {
    const hasBackendURL = !!backendURL;
    const isExpiredSession = expired && hasBackendURL;

    return (
      <div
        style={{
          height: "100vh",
          width: "100vw",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span style={{ fontSize: "16px" }}>
          <b>ERROR 403 - </b> ACCESS DENIED
        </span>
        <span
          style={{
            fontSize: "12px",
            marginBottom: "10px",
            textAlign: "center",
          }}
        >
          {isExpiredSession
            ? "Your session has expired. Please log in again."
            : "You have no access to this page."}
        </span>
        {isExpiredSession && (
          <button
            style={{
              marginTop: "10px",
              backgroundColor: "#777",
              color: "#fff",
              border: "none",
              padding: "5px 10px",
              fontSize: "12px",
              borderRadius: "30px",
              cursor: "pointer",
            }}
            onClick={handleLogin}
          >
            Continue
          </button>
        )}
      </div>
    );
  }

  return (
    <Provider store={store}>
      <ShoppingCartProvider>
        <BrowserRouter basename="/">
          <Routes>
            <Route
              path="/"
              element={
                <Sentry.ErrorBoundary fallback={<BuilderErrorFallback />}>
                  <Suspense fallback={null}>
                    <Builder title="Builder" />
                  </Suspense>
                </Sentry.ErrorBoundary>
              }
              exact
            />
          </Routes>
        </BrowserRouter>
      </ShoppingCartProvider>
    </Provider>
  );
}
